import { ref, onBeforeUnmount, nextTick } from 'vue';
import { createPopper } from '@popperjs/core';

const DEFAULT_OFFSET = 12;

export function useTeleportedTooltip(getOptions) {
  const triggerRef = ref(null);
  const popperRef = ref(null);
  const arrowRef = ref(null);
  const isOpen = ref(false);
  let popperInstance = null;

  const resolveOptions = () => (
    typeof getOptions === 'function' ? getOptions() : getOptions
  );

  const destroyPopper = () => {
    if (popperInstance) {
      popperInstance.destroy();
      popperInstance = null;
    }
  };

  const updatePopper = () => {
    popperInstance?.update?.();
  };

  const initPopper = async () => {
    destroyPopper();
    await nextTick();

    if (!triggerRef.value || !popperRef.value) {
      return;
    }

    const opts = resolveOptions();
    const placement = opts.placement || 'top';
    const offsetDistance = Number(opts.offsetDistance) || DEFAULT_OFFSET;

    popperInstance = createPopper(triggerRef.value, popperRef.value, {
      placement,
      strategy: 'fixed',
      modifiers: [
        {
          name: 'arrow',
          options: {
            element: arrowRef.value,
            padding: 8,
          },
        },
        {
          name: 'offset',
          options: { offset: [0, offsetDistance] },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [
              'top',
              'bottom',
              'left',
              'right',
              'top-start',
              'top-end',
              'bottom-start',
              'bottom-end',
            ],
          },
        },
        {
          name: 'preventOverflow',
          options: { padding: 8, rootBoundary: 'viewport' },
        },
      ],
    });

    updatePopper();
  };

  const openTooltip = async () => {
    if (isOpen.value) {
      updatePopper();
      return;
    }
    isOpen.value = true;
    await initPopper();
  };

  const closeTooltip = () => {
    isOpen.value = false;
    destroyPopper();
  };

  const onScrollOrResize = () => {
    if (isOpen.value) {
      updatePopper();
    }
  };

  window.addEventListener('scroll', onScrollOrResize, true);
  window.addEventListener('resize', onScrollOrResize);

  onBeforeUnmount(() => {
    closeTooltip();
    window.removeEventListener('scroll', onScrollOrResize, true);
    window.removeEventListener('resize', onScrollOrResize);
  });

  return {
    triggerRef,
    popperRef,
    arrowRef,
    isOpen,
    openTooltip,
    closeTooltip,
    updatePopper,
  };
}
